"use client";

import {
  Children,
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { ArTableContext, useArTableContext } from "./context";
import { HorizontalScrollbar } from "./scrollbar";
import { ArTablePagination } from "./pagination";
import type { ColumnConfig, ArTableStore } from "./types";

// ─── Helpers shared across sub-components ──────────────────────

function getVisibleCols(
  columns: ColumnConfig[],
  hidden: Set<string>,
): ColumnConfig[] {
  return [...columns]
    .filter((c) => c.visible && !hidden.has(c.id))
    .sort((a, b) => a.position - b.position);
}

/**
 * Fallback used only if a column width is somehow non-finite. The store
 * reconciles widths on hydration, but this guarantees one bad value can never
 * NaN the table total and collapse every column under `table-layout: fixed`.
 */
const FALLBACK_COL_WIDTH = 120;

function colWidth(col: ColumnConfig): number {
  return Number.isFinite(col.width) && col.width > 0
    ? col.width
    : FALLBACK_COL_WIDTH;
}

function makeCellStyle(
  col: ColumnConfig,
  stickyLeft: Map<string, number>,
): React.CSSProperties {
  const w = colWidth(col);
  return {
    width: w,
    minWidth: w,
    maxWidth: w,
    left: stickyLeft.get(col.id),
  };
}

/**
 * Map consumer children (in defaultColumns order) to a lookup keyed by column id.
 * The i-th child is assumed to correspond to defaultColumns[i]. This lets
 * consumers write children in a natural order while the store freely
 * reorders / hides columns.
 */
function childrenToColMap(
  children: ReactNode,
  defaultColumns: ColumnConfig[],
): Map<string, ReactNode> {
  const map = new Map<string, ReactNode>();
  const arr = Children.toArray(children);
  defaultColumns.forEach((def, i) => {
    map.set(def.id, arr[i]);
  });
  return map;
}

const SHADOW_CLS =
  "after:content-[''] after:absolute after:top-0 after:right-[-6px] after:bottom-0 after:w-[6px] after:pointer-events-none after:bg-gradient-to-r after:from-black/20 after:to-transparent";

/** Header row height (px); keep in sync with the `h-[45px]` in `thCls`. */
const HEAD_HEIGHT = 45;

function thCls(
  col: ColumnConfig,
  stickyColumns: Set<string>,
  lastStickyId: string | null,
  isScrolledRight: boolean,
): string {
  const sticky = stickyColumns.has(col.id);
  const isLast = col.id === lastStickyId;
  const pad =
    col.id === "select" ? "px-0" : col.id === "name" ? "px-4" : "px-2";
  const align = col.id === "select" ? "text-center" : "text-left";
  return [
    `h-[45px] ${pad} ${align} align-middle text-sm font-medium text-muted-foreground whitespace-nowrap relative`,
    "border-b border-border border-r border-border bg-surface-elevated",
    sticky ? `sticky ${isLast ? "z-10" : "z-20"}` : "",
    isLast && isScrolledRight ? SHADOW_CLS : "",
  ].join(" ");
}

function tdCls(
  col: ColumnConfig,
  stickyColumns: Set<string>,
  lastStickyId: string | null,
  isScrolledRight: boolean,
): string {
  const sticky = stickyColumns.has(col.id);
  const isLast = col.id === lastStickyId;
  const pad =
    col.id === "select" ? "px-0" : col.id === "name" ? "px-4" : "px-2";
  return [
    `h-[42px] ${pad} align-middle border-b border-border border-r border-border overflow-hidden`,
    "group-hover/row:bg-surface-header",
    sticky ? `sticky ${isLast ? "z-20" : "z-30"} bg-surface-primary` : "",
    isLast && isScrolledRight ? `relative ${SHADOW_CLS}` : "",
  ].join(" ");
}

function fillerTdCls(
  col: ColumnConfig,
  stickyColumns: Set<string>,
  lastStickyId: string | null,
  isScrolledRight: boolean,
): string {
  const sticky = stickyColumns.has(col.id);
  const isLast = col.id === lastStickyId;
  return [
    "p-0 border-r border-border",
    sticky ? `sticky ${isLast ? "z-20" : "z-30"} bg-surface-primary` : "",
    isLast && isScrolledRight ? `relative ${SHADOW_CLS}` : "",
  ].join(" ");
}

// ─── ArTable.Root ──────────────────────────────────────────────

interface RootProps {
  store: () => ArTableStore;
  /**
   * Column ids to force-hide for this table instance only. Excluded from
   * rendering without touching the store's persisted `visible` flag, so the
   * hide never leaks into the user's saved column prefs (which are shared
   * across all instances of the store).
   */
  hiddenColumns?: string[];
  children: ReactNode;
}

function Root({ store: useStore, hiddenColumns: hiddenColumnsProp, children }: RootProps) {
  const { columns, stickyColumns } = useStore();

  // Content-keyed so an inline `["sync"]` literal doesn't rebuild the set (and
  // the whole context) on every render.
  const hiddenKey = (hiddenColumnsProp ?? []).join(",");
  const hiddenColumns = useMemo(
    () => new Set(hiddenKey ? hiddenKey.split(",") : []),
    [hiddenKey],
  );

  const visibleCols = useMemo(
    () => getVisibleCols(columns, hiddenColumns),
    [columns, hiddenColumns],
  );

  const stickyLeft = useMemo(() => {
    const map = new Map<string, number>();
    let left = 0;
    for (const col of visibleCols) {
      if (stickyColumns.has(col.id)) {
        map.set(col.id, left);
        left += colWidth(col);
      }
    }
    return map;
  }, [visibleCols, stickyColumns]);

  const lastStickyId = useMemo(() => {
    const sticky = visibleCols.filter((c) => stickyColumns.has(c.id));
    return sticky.length > 0 ? sticky[sticky.length - 1].id : null;
  }, [visibleCols, stickyColumns]);

  const totalColWidth = useMemo(
    () => visibleCols.reduce((sum, c) => sum + colWidth(c), 0),
    [visibleCols],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolledRight, setIsScrolledRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setIsScrolledRight(el.scrollLeft > 0);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const ctx = useMemo(
    () => ({
      useStore,
      hiddenColumns,
      stickyLeft,
      lastStickyId,
      totalColWidth,
      scrollRef,
      isScrolledRight,
    }),
    [useStore, hiddenColumns, stickyLeft, lastStickyId, totalColWidth, isScrolledRight],
  );

  return (
    <ArTableContext.Provider value={ctx}>
      <div className="flex flex-col flex-1 min-h-0">{children}</div>
    </ArTableContext.Provider>
  );
}

// ─── ArTable.Table ─────────────────────────────────────────────

interface TableProps {
  children: ReactNode;
}

function Table({ children }: TableProps) {
  const { useStore, hiddenColumns, scrollRef, totalColWidth } =
    useArTableContext();
  const { columns } = useStore();
  const visibleCols = useMemo(
    () => getVisibleCols(columns, hiddenColumns),
    [columns, hiddenColumns],
  );

  return (
    <div
      ref={scrollRef}
      data-slot="ar-table-scroll"
      className="flex-1 min-h-0 bg-surface-primary pipeline-scrollbar"
    >
      <table
        className="text-sm"
        style={{
          borderSpacing: 0,
          borderCollapse: "separate",
          tableLayout: "fixed",
          height: "100%",
          width: totalColWidth,
          minWidth: "100%",
        }}
      >
        <colgroup>
          {visibleCols.map((col) => {
            const w = colWidth(col);
            return (
              <col
                key={col.id}
                style={{ width: w, minWidth: w, maxWidth: w }}
              />
            );
          })}
        </colgroup>
        {children}
      </table>
    </div>
  );
}

// ─── ArTable.Head ──────────────────────────────────────────────
// Consumer provides children in defaultColumns order (one child per column).
// Head wraps each in a <th> and adds sort button + resize handle as needed.

interface HeadProps {
  sortable?: string[];
  children: ReactNode;
}

function Head({ sortable, children }: HeadProps) {
  const { useStore, hiddenColumns, stickyLeft, lastStickyId, isScrolledRight } =
    useArTableContext();
  const {
    columns,
    defaultColumns,
    sorts,
    setSort,
    setColumnWidth,
    fixedColumns,
    stickyColumns,
  } = useStore();

  const sortableSet = useMemo(() => new Set(sortable ?? []), [sortable]);

  const visibleCols = useMemo(
    () => getVisibleCols(columns, hiddenColumns),
    [columns, hiddenColumns],
  );
  const childByColId = useMemo(
    () => childrenToColMap(children, defaultColumns),
    [children, defaultColumns],
  );

  const [resizingColId, setResizingColId] = useState<string | null>(null);
  const [resizeHoverColId, setResizeHoverColId] = useState<string | null>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, col: ColumnConfig) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = colWidth(col);
      const colId = col.id;
      const minW = colId === "name" ? 120 : 60;

      setResizingColId(colId);
      setResizeHoverColId(null);

      const handleMove = (moveEvent: MouseEvent) => {
        setColumnWidth(
          colId,
          Math.max(minW, startWidth + (moveEvent.clientX - startX)),
        );
      };

      const handleUp = () => {
        setResizingColId(null);
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [setColumnWidth],
  );

  function renderHeaderContent(col: ColumnConfig) {
    const content = childByColId.get(col.id);
    if (!sortableSet.has(col.id)) return content;

    const sortIdx = sorts.findIndex((s) => s.column === col.id);
    const activeEntry = sortIdx >= 0 ? sorts[sortIdx] : null;
    const sortPosition = sortIdx >= 0 ? sortIdx + 1 : null;
    const showPosition = sorts.length > 1 && sortPosition !== null;

    return (
      <button
        type="button"
        className="flex items-center gap-1 cursor-pointer hover:text-heading transition-colors w-full group/sort"
        onClick={() => setSort(col.id)}
      >
        {content}
        <span className="ml-auto shrink-0 flex items-center gap-0.5">
          {activeEntry?.order === "desc" && (
            <Icon name="IconArrowDown" size={14} className="text-contrast/50" />
          )}
          {activeEntry?.order === "asc" && (
            <Icon name="IconArrowUp" size={14} className="text-contrast/50" />
          )}
          {!activeEntry && (
            <Icon
              name="IconArrowBottomTop"
              size={14}
              className="text-contrast/30 opacity-0 group-hover/sort:opacity-100 transition-opacity"
            />
          )}
          {showPosition && (
            <span className="flex size-[14px] items-center justify-center rounded-full bg-contrast/10 text-[10px] font-medium text-contrast/60">
              {sortPosition}
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <thead className="sticky top-0 z-40">
      <tr>
        {visibleCols.map((col) => {
          const isFixed = fixedColumns.has(col.id);
          const isActive = resizingColId === col.id;
          const isPreview = resizeHoverColId === col.id;
          const showGuide = (isActive || isPreview) && !isFixed;
          const guideCls = isActive
            ? "bg-ai shadow-[0_0_8px_color-mix(in_oklab,var(--ai)_50%,transparent)]"
            : "bg-ai/40";
          return (
            <th
              key={col.id}
              className={thCls(
                col,
                stickyColumns,
                lastStickyId,
                isScrolledRight,
              )}
              style={makeCellStyle(col, stickyLeft)}
            >
              {renderHeaderContent(col)}
              {!isFixed && (
                <div
                  className="absolute top-0 bottom-0 w-4 cursor-col-resize z-10"
                  style={{ right: -8 }}
                  onMouseDown={(e) => handleResizeStart(e, col)}
                  onMouseEnter={() => {
                    if (!resizingColId) setResizeHoverColId(col.id);
                  }}
                  onMouseLeave={() => setResizeHoverColId(null)}
                />
              )}
              {showGuide && (
                <div
                  className={`absolute right-0 top-0 bottom-0 w-[2px] pointer-events-none z-20 ${guideCls}`}
                />
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

// ─── ArTable.Body ──────────────────────────────────────────────

export interface BodyVirtualizeOptions {
  /** Row height guess before measurement (px). Expanded rows self-correct. */
  estimateSize: number;
  overscan?: number;
  /**
   * Fired when the last rendered item is within `endReachedOffset` rows of
   * the end — the hook for "load the next page".
   */
  onEndReached?: () => void;
  endReachedOffset?: number;
}

interface BodyProps<TRow> {
  data: TRow[];
  loading?: boolean;
  emptyMessage?: string;
  skeletonRows?: number;
  /**
   * Render only the rows near the viewport. Each item is wrapped in its own
   * `<tbody>` (valid inside `<table>`) so a row plus its expanded detail row
   * measure as one block. Off by default — small tables keep the plain path.
   */
  virtualize?: BodyVirtualizeOptions;
  /** Rendered after the last row (e.g. a "loading more" indicator). */
  trailing?: ReactNode;
  children: (row: TRow, index: number) => ReactNode;
}

// Deterministic pseudo-width so skeleton bars aren't all the same length.
const SKELETON_WIDTHS = [72, 84, 60, 78, 66, 90, 54, 74, 86, 62];

function Body<TRow>({
  data,
  loading,
  emptyMessage = "No data found.",
  skeletonRows = 10,
  virtualize,
  trailing,
  children,
}: BodyProps<TRow>) {
  const {
    useStore,
    hiddenColumns,
    stickyLeft,
    lastStickyId,
    isScrolledRight,
    scrollRef,
  } = useArTableContext();
  const { columns, stickyColumns } = useStore();

  const visibleCols = useMemo(
    () => getVisibleCols(columns, hiddenColumns),
    [columns, hiddenColumns],
  );

  const virtualEnabled = !!virtualize && !loading && data.length > 0;
  const estimate = virtualize?.estimateSize ?? 42;
  const overscan = virtualize?.overscan ?? 8;
  const virtualizer = useVirtualizer({
    count: virtualEnabled ? data.length : 0,
    enabled: virtualEnabled,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimate,
    overscan,
    // Items start below the sticky header, which shares the scroll element.
    scrollMargin: HEAD_HEIGHT,
  });
  const measuredItems = virtualizer.getVirtualItems();

  // Until the virtualizer has measured the scroll element (first paint, or
  // right after `enabled` flips on once data lands) its range is empty. Rather
  // than paint a blank table while waiting for its notify, show a screenful
  // from the top with estimated positions — the real range replaces it on the
  // next render.
  const virtualItems = useMemo(() => {
    if (!virtualEnabled || measuredItems.length > 0) return measuredItems;
    const n = Math.min(data.length, Math.ceil(900 / estimate) + overscan);
    return Array.from({ length: n }, (_, i) => ({
      key: i,
      index: i,
      lane: 0,
      size: estimate,
      start: HEAD_HEIGHT + i * estimate,
      end: HEAD_HEIGHT + (i + 1) * estimate,
    }));
  }, [virtualEnabled, measuredItems, data.length, estimate, overscan]);

  // Belt and braces for the same window: once enabled, force a measurement
  // pass on the next frame so a scroll rect read before layout settled (or
  // missed while the panel-collapse springs ran) can't leave a stale range.
  useEffect(() => {
    if (!virtualEnabled) return;
    const id = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el && !(virtualizer.scrollRect && virtualizer.scrollRect.height > 0)) {
        virtualizer.scrollRect = {
          width: el.offsetWidth,
          height: el.offsetHeight,
        };
      }
      virtualizer.measure();
    });
    return () => cancelAnimationFrame(id);
  }, [virtualEnabled, virtualizer, scrollRef]);

  // "Load more" trigger, keyed on the last rendered index so a growing
  // `data` array (page appended) re-arms it without firing per scroll tick.
  const lastRenderedIndex = virtualItems.length
    ? virtualItems[virtualItems.length - 1].index
    : -1;
  const onEndReached = virtualize?.onEndReached;
  const endReachedOffset = virtualize?.endReachedOffset ?? 5;
  useEffect(() => {
    if (!virtualEnabled || !onEndReached) return;
    if (lastRenderedIndex < 0) return;
    if (lastRenderedIndex >= data.length - 1 - endReachedOffset) {
      onEndReached();
    }
  }, [virtualEnabled, onEndReached, lastRenderedIndex, data.length, endReachedOffset]);

  const fillerRow = (
    <tr aria-hidden className="h-full">
      {visibleCols.map((col) => (
        <td
          key={col.id}
          className={`h-full ${fillerTdCls(
            col,
            stickyColumns,
            lastStickyId,
            isScrolledRight,
          )}`}
          style={{ left: stickyLeft.get(col.id) }}
        />
      ))}
    </tr>
  );

  if (virtualEnabled) {
    // `start`/`end` include `scrollMargin`; `getTotalSize()` does not. When
    // we're on the estimated fallback the virtualizer's total is 0, so size
    // the tail from the estimate instead.
    const first = virtualItems[0];
    const last = virtualItems[virtualItems.length - 1];
    const total =
      measuredItems.length > 0
        ? virtualizer.getTotalSize()
        : data.length * estimate;
    const padTop = first ? Math.max(0, first.start - HEAD_HEIGHT) : 0;
    const padBottom = last
      ? Math.max(0, total - (last.end - HEAD_HEIGHT))
      : 0;
    // Spacers carry a real cell: a cell-less <tr> isn't guaranteed to honour
    // its height in every engine.
    const spacer = (height: number) => (
      <tbody aria-hidden>
        <tr>
          <td
            colSpan={visibleCols.length}
            className="p-0 border-0"
            style={{ height }}
          />
        </tr>
      </tbody>
    );
    return (
      <>
        {padTop > 0 && spacer(padTop)}
        {virtualItems.map((item) => (
          <tbody
            key={item.key}
            data-index={item.index}
            ref={virtualizer.measureElement}
          >
            {children(data[item.index], item.index)}
          </tbody>
        ))}
        {padBottom > 0 && spacer(padBottom)}
        <tbody>
          {trailing}
          {fillerRow}
        </tbody>
      </>
    );
  }

  return (
    <tbody>
      {loading ? (
        Array.from({ length: skeletonRows }).map((_, index) => (
          <tr key={index} className="group/row">
            {visibleCols.map((col, colIdx) => {
              const w =
                SKELETON_WIDTHS[(index + colIdx * 3) % SKELETON_WIDTHS.length];
              return (
                <td
                  key={col.id}
                  className={tdCls(
                    col,
                    stickyColumns,
                    lastStickyId,
                    isScrolledRight,
                  )}
                  style={makeCellStyle(col, stickyLeft)}
                >
                  {col.id === "select" ? (
                    <div className="flex items-center justify-center">
                      <div className="size-4 rounded bg-contrast/6 shimmer shimmer-bg text-foreground" />
                    </div>
                  ) : (
                    <div
                      className="h-3.5 rounded bg-contrast/6 shimmer shimmer-bg text-foreground"
                      style={{ width: `${w}%` }}
                    />
                  )}
                </td>
              );
            })}
          </tr>
        ))
      ) : data.length === 0 ? (
        <tr>
          <td
            colSpan={visibleCols.length}
            className="text-center py-16 text-contrast/40 text-sm border-b border-border"
          >
            {emptyMessage}
          </td>
        </tr>
      ) : (
        data.map((row, index) => children(row, index))
      )}
      {!loading && data.length > 0 ? trailing : null}
      {fillerRow}
    </tbody>
  );
}

// ─── ArTable.Row ───────────────────────────────────────────────
// Consumer provides children in defaultColumns order (one per column).
// Row wraps each in a <td> with width/sticky/shadow styling.
// All native <tr> attributes (onClick, data-*, aria-*, ...) are forwarded.

interface RowProps extends React.ComponentProps<"tr"> {
  children: ReactNode;
}

function Row({ children, className, ...rest }: RowProps) {
  const { useStore, hiddenColumns, stickyLeft, lastStickyId, isScrolledRight } =
    useArTableContext();
  const { columns, defaultColumns, stickyColumns } = useStore();

  const visibleCols = useMemo(
    () => getVisibleCols(columns, hiddenColumns),
    [columns, hiddenColumns],
  );
  const childByColId = useMemo(
    () => childrenToColMap(children, defaultColumns),
    [children, defaultColumns],
  );

  return (
    <tr
      {...rest}
      className={`cursor-pointer transition-colors group/row ${className ?? ""}`}
    >
      {visibleCols.map((col) => (
        <td
          key={col.id}
          className={tdCls(col, stickyColumns, lastStickyId, isScrolledRight)}
          style={makeCellStyle(col, stickyLeft)}
        >
          {childByColId.get(col.id)}
        </td>
      ))}
    </tr>
  );
}

// ─── ArTable.Scrollbar ─────────────────────────────────────────

function Scrollbar() {
  const { scrollRef } = useArTableContext();
  return <HorizontalScrollbar scrollRef={scrollRef} />;
}

// ─── ArTable.Footer ────────────────────────────────────────────

interface FooterProps {
  totalCount: number;
  perPage: number;
  disabled?: boolean;
  children?: ReactNode;
  /** Optional content shown on mobile only (where `children` is hidden), left
   *  of the pagination — e.g. a compact total count. */
  mobileStart?: ReactNode;
}

function Footer({
  totalCount,
  perPage,
  disabled,
  children,
  mobileStart,
}: FooterProps) {
  const { useStore } = useArTableContext();
  const { page, setPage } = useStore();
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  return (
    <div
      className={cn(
        "flex items-center gap-2 border-t border-border bg-surface-elevated px-4 h-14 lg:h-10 shrink-0 sm:justify-between",
        mobileStart ? "justify-between" : "justify-center",
      )}
    >
      <div className="hidden min-w-0 truncate sm:block">{children}</div>
      {mobileStart ? (
        <div className="min-w-0 truncate sm:hidden">{mobileStart}</div>
      ) : null}
      <ArTablePagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={disabled}
      />
    </div>
  );
}

// ─── Public compound export ────────────────────────────────────

export const ArTable = {
  Root,
  Table,
  Head,
  Body,
  Row,
  Footer,
  Scrollbar,
};

export type { ColumnConfig, SortEntry, ArTableStore } from "./types";
export { createArTableStore } from "./use-ar-table-store";
export type { ArTableStoreConfig } from "./use-ar-table-store";
export { useArTableSelection } from "./use-ar-table-selection";
export type {
  ArTableSelection,
  UseArTableSelectionConfig,
} from "./use-ar-table-selection";
